import { yupResolver } from "@hookform/resolvers/yup";
import { UpdateTrancheRequest, UserPaymentTrancheApi, userPaymentsApi } from "api";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import {
  PaziaButton,
  PaziaDateSelect,
  PaziaFormButton,
  PaziaInput,
} from "components/atoms";
import { PaziaModal } from "components/molecules";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getApiErrorMessage, useToaster } from "utils";
import * as yup from "yup";

interface EditTrancheModalProps {
  onCloseRequest: () => void;
  tranche?: UserPaymentTrancheApi;
  userId: number;
  courseId: number;
}

export const EditTrancheModal = ({ onCloseRequest, tranche, userId, courseId }: EditTrancheModalProps) => {
  const { showError, showSuccess } = useToaster();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const onSubmit = async (data: FormValues) => {
    try {
      if (!tranche) return;

      const requestBody: UpdateTrancheRequest = {
        name: data.name,
        amount: data.amount,
        dueDate: data.dueDate,
        discountedAmount: data.amount,
        paymentId: data.paymentId || undefined,
      };

      await userPaymentsApi.updateTranche(userId, courseId, tranche.courseTrancheId, requestBody);
      showSuccess("Transza została zaktualizowana");
    } catch (error) {
      console.error(error);
      showError("Wystąpił błąd podczas aktualizacji transzy");
    } finally {
      onCloseRequest();
    }
  };

  const handleDelete = async () => {
    try {
      if (!tranche) return;
      setIsDeleting(true);
      await userPaymentsApi.deleteTranche(userId, courseId, tranche.courseTrancheId, tranche.name);
      showSuccess("Transza została usunięta");
      onCloseRequest();
    } catch (error) {
      console.error(error);
      showError(getApiErrorMessage(error, "Wystąpił błąd podczas usuwania transzy"));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <PaziaModal id="edit-tranche-modal" onCloseRequest={onCloseRequest} isOpen={!!tranche} size="2xl">
      {tranche && (
        <ModalPadding>
          <ModalHeader>Edytuj transzę</ModalHeader>
          <EditTrancheForm
            onSubmit={onSubmit}
            onCancel={onCloseRequest}
            tranche={tranche}
            onDelete={() => setShowDeleteConfirm(true)}
            showDeleteConfirm={showDeleteConfirm}
            onConfirmDelete={handleDelete}
            onCancelDelete={() => setShowDeleteConfirm(false)}
            isDeleting={isDeleting}
          />
        </ModalPadding>
      )}
    </PaziaModal>
  );
};

const EditTrancheForm = ({
  onSubmit,
  onCancel,
  tranche,
  onDelete,
  showDeleteConfirm,
  onConfirmDelete,
  onCancelDelete,
  isDeleting,
}: {
  onSubmit: (data: FormValues) => Promise<void>;
  onCancel: () => void;
  tranche: UserPaymentTrancheApi;
  onDelete: () => void;
  showDeleteConfirm: boolean;
  onConfirmDelete: () => Promise<void>;
  onCancelDelete: () => void;
  isDeleting: boolean;
}) => {
  const schema = yup
    .object({
      name: yup.string().required("Nazwa transzy jest wymagana"),
      amount: yup.number().required("Kwota jest wymagana").min(0, "Kwota musi być większa lub równa 0"),
      dueDate: yup.date().required("Data płatności jest wymagana"),
      paymentId: yup.number().nullable().notRequired(),
    })
    .required();

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: tranche.name,
      amount: tranche.amount,
      dueDate: new Date(tranche.dueDate),
      paymentId: tranche.paymentId || null,
    },
  });
  const { handleSubmit } = methods;
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
          <PaziaInput name="name" label="Nazwa transzy" placeholder="Wprowadź nazwę transzy" />
          <PaziaInput name="amount" label="Kwota (PLN)" placeholder="0.00" />
          <PaziaDateSelect name="dueDate" label="Data płatności" />

          <PaziaInput
            name="paymentId"
            label="ID Płatności (opcjonalne)"
            type="number"
            placeholder="ID powiązanej płatności"
          />
        </div>

        {!showDeleteConfirm ? (
          <div className="mt-4 flex items-center justify-between gap-2">
            <PaziaButton btnType="button" type="danger" onClick={onDelete} title="Usuń transzę" />
            <div className="flex gap-2">
              <PaziaButton btnType="button" type="tertiary" onClick={onCancel} title="Anuluj" />
              <PaziaFormButton type="success" title="Zapisz zmiany" />
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border-2 border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <div className="mb-3 text-center font-semibold text-red-700 dark:text-red-400">
              Czy na pewno chcesz usunąć tę transzę?
            </div>
            <div className="mb-4 text-center text-sm text-red-600 dark:text-red-400">
              Ta operacja jest nieodwracalna. Transza "{tranche.name}" zostanie trwale usunięta.
            </div>
            <div className="flex justify-center gap-2">
              <PaziaButton
                btnType="button"
                type="tertiary"
                onClick={onCancelDelete}
                title="Anuluj"
                disabled={isDeleting}
              />
              <PaziaButton
                btnType="button"
                type="danger"
                onClick={onConfirmDelete}
                title={isDeleting ? "Usuwanie..." : "Tak, usuń transzę"}
                disabled={isDeleting}
              />
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

type FormValues = {
  name: string;
  amount: number;
  dueDate: Date;
  paymentId?: number | null;
};
